import type { ReactElement, ReactNode } from 'react';
import { useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import StackIcon from 'tech-stack-icons';

import { browserSupportGuardStorageKey } from '../../../config/component';
import { isSupportedBrowser } from '../../../services/browser';
import useLocalStorage from '../../../services/hooks/useLocalStorage';

import Rows from '../../atoms/Rows';
import Body from '../../molecules/Body';
import CTAButton from '../../molecules/CTAButton';
import Header from '../../molecules/Header';
import ConfirmationModal from '../ConfirmationModal';

type Props = Readonly<{
  children: ReactNode;
}>;

const InlineLogo = styled.span`
  display: inline-flex;
  height: 1.1em;
  width: 1.1em;
  margin-right: 0.2em;
  vertical-align: text-bottom;

  & svg {
    height: 100%;
    width: 100%;
  }
`;

const BrowserSupportGuard = ({ children }: Props): ReactElement => {
  const { t } = useTranslation();

  const [supported] = useState(isSupportedBrowser);
  const [dismissed, setDismissed] = useLocalStorage(browserSupportGuardStorageKey, false);

  const dismiss = () => {
    setDismissed(true);
  };

  return (
    <>
      {!supported && !dismissed && (
        <ConfirmationModal
          closeConfirms
          onConfirm={dismiss}
          header={<Header as="h4">{t('Your browser is not officially supported')}</Header>}
          body={(
            <Rows gap={500}>
              <Body size={300} display="inline">
                <Trans
                  defaults="We officially support <chrome/>Chrome and <firefox/>Firefox."
                  components={{
                    chrome: <InlineLogo><StackIcon name="chrome" /></InlineLogo>,
                    firefox: <InlineLogo><StackIcon name="firefox" /></InlineLogo>,
                  }}
                />
              </Body>
              <Body size={300} display="inline">
                <Trans
                  defaults="On other browsers, such as Safari, <warn>you may experience visual glitches, crashes, or instability.</warn>"
                  components={{
                    warn: <Body size={300} variant="alert" display="inline" />,
                  }}
                />
              </Body>
              <Body size={300} display="inline" textDecoration="underline">
                {t('For the best experience, please use Chrome or Firefox.')}
              </Body>
            </Rows>
          )}
          confirmButton={(
            <CTAButton
              onClick={dismiss}
              ref={(node) => node?.focus()}
            >
              {t('I understand')}
            </CTAButton>
          )}
        />
      )}
      {children}
    </>
  );
};

export default BrowserSupportGuard;
